import React, { useContext, useEffect, useState, useMemo } from 'react';
import { AuthContext } from './../AuthContext';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { site_url } from "./../config.js";
import axios from 'axios';
import { Link, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { FormatDate } from './../Function';

function DenominationList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [denominations, setDenominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('all');

  useEffect(() => {
    fetchRoutes();
    fetchDenominations();
  }, [user.id]);

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(site_url + '/shop_cates');
      if (response.data && response.data.status === 'success') {
        setRoutes(response.data.shop_cate || []);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchDenominations = async () => {
    setLoading(true);
    try {
      // Call the API endpoint that returns JSON denomination list
      const response = await axios.post(site_url + '/get-denominations-list', {
        w_id: user.w_type == 1 ? null : user.id,
        fromdate: fromDate,
        todate: toDate,
        routeid: selectedRoute === 'all' ? null : selectedRoute
      });
      
      if (response.data && response.data.status === 'success') {
        setDenominations(response.data.denominations || []);
      }
    } catch (error) {
      console.error('Error fetching denominations:', error);
      // Fallback: try to parse from HTML endpoint if JSON endpoint doesn't exist
      Swal.fire({
        title: 'Info',
        text: 'Please create the API endpoint /api/get-denominations-list to fetch denominations as JSON',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      setDenominations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (denomination) => {
    // Navigate to Denominationlistcal for editing
    navigate(`/Denominationlistcal?id=${denomination.id}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.get(`${site_url}/delete_denomination/${id}`);
        if (response.data && response.data.status === 'success') {
          Swal.fire('Deleted!', 'Denomination has been deleted.', 'success');
          fetchDenominations();
        } else {
          Swal.fire('Error!', response.data.message || 'Failed to delete denomination.', 'error');
        }
      } catch (error) {
        Swal.fire('Error!', 'Failed to delete denomination.', 'error');
        console.error('Delete error:', error);
      }
    }
  };

  const handleFilter = () => {
    fetchDenominations();
  };

  // Calculate totals based on user's field mapping
  // Route Total = totalcollectionamount, Collection Total = totalrouteamount
  const totalRouteAmount = useMemo(() => {
    return denominations.reduce((sum, denom) => {
      return sum + parseFloat(denom.totalcollectionamount || 0);
    }, 0);
  }, [denominations]);

  const totalCollectionAmount = useMemo(() => {
    return denominations.reduce((sum, denom) => {
      return sum + parseFloat(denom.totalrouteamount || 0);
    }, 0);
  }, [denominations]);

  const totalBalance = useMemo(() => {
    return totalRouteAmount - totalCollectionAmount;
  }, [totalRouteAmount, totalCollectionAmount]);

  return (
    <>
      <div id="appCapsule">
        <div className="section mt-4">
          <div className="section-heading mb-3">
            <h2>Denomination List</h2>
          </div>

          {/* Filter Section */}
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      max={toDate}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      min={fromDate}
                    />
                  </div>
                </div>
                {user.w_type == 1 && (
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Route</label>
                      <select
                        className="form-control"
                        value={selectedRoute}
                        onChange={(e) => setSelectedRoute(e.target.value)}
                      >
                        <option value="all">All Routes</option>
                        {routes.map((route) => (
                          <option key={route.id} value={route.id}>
                            {route.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleFilter}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Filter'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Denominations List */}
          <div className="transactions mt-3">
            {loading ? (
              <div className="text-center p-4">
                <p>Loading denominations...</p>
              </div>
            ) : denominations.length === 0 ? (
              <div className="text-center p-4">
                <p>No denominations found for the selected criteria.</p>
              </div>
            ) : (
              denominations.map((denomination) => (
                <div key={denomination.id} className="item">
                  <div className="detail">
                    <div>
                      <strong>
                        {denomination.route_name || `Route ID: ${denomination.routeid}`}
                      </strong>
                      <p className="mb-1">
                        Date: {FormatDate(denomination.collectiondate) || denomination.collectiondate}
                      </p>
                      <div className="row mt-2">
                        <div className="col-6">
                          <small>
                            ₹500: {denomination['500amount'] || 0} | 
                            ₹200: {denomination['200amount'] || 0} | 
                            ₹100: {denomination['100amount'] || 0}
                          </small>
                        </div>
                        <div className="col-6">
                          <small>
                            ₹50: {denomination['50amount'] || 0} | 
                            ₹20: {denomination['20amount'] || 0} | 
                            ₹10: {denomination['10amount'] || 0} | 
                            Coins: ₹{denomination['coinsamount'] || 0}
                          </small>
                        </div>
                      </div>
                      <h4 className="mt-2">
                       Route Total: ₹{denomination.totalcollectionamount || 0}
                      </h4>
                      <h4 className="mt-2">
                       Collection Total: ₹{ denomination.totalrouteamount || 0}
                       <br />
                       <small className=" text-danger" style={{fontSize:'12px',color:'red'}}>
                        (Route Total - Collection Total = Balance) {denomination.totalcollectionamount - denomination.totalrouteamount}
                       </small>
                      </h4>
                    </div>
                  </div>
                  <div className="right">
                    <div className="d-flex flex-column gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(denomination)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(denomination.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Floating Bottom Total Balance */}
      {denominations.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          padding: '15px 20px',
          zIndex: 1000,
          borderTop: '2px solid #4B49AC'
        }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-3">
                <strong>Total Route Amount:</strong>
                <h4 className="mb-0 text-primary">₹ {totalRouteAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
              </div>
              <div className="col-md-3">
                <strong>Total Collection Amount:</strong>
                <h4 className="mb-0 text-success">₹ {totalCollectionAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
              </div>
              <div className="col-md-6 text-end">
                <strong>Total Balance:</strong>
                <h3 className={`mb-0 ${totalBalance >= 0 ? 'text-warning' : 'text-danger'}`}>
                  ₹ {totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h3>
                <small className="text-muted">(Route Total - Collection Total)</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add bottom padding to prevent content from being hidden behind floating footer */}
      <div style={{ height: denominations.length > 0 ? '120px' : '0' }}></div>

      <Footer />
      <Sidebar />
    </>
  );
}

export default DenominationList;
